import { flowRight, uniqBy, compact } from 'lodash';
import * as React from 'react';

import * as I18n from 'i18n-js';
import { ChildProps, MutateProps, graphql } from 'react-apollo';
import bind from '@reverbdotcom/commons/dist/bind';
import {
  SelectableCreditCardListQuery,
  core_apimessages_CreditCard,
} from '../schemas/rql';
import {
  connectSelectableCreditCardListQuery,
  QUERY,
} from './selectable_credit_card_list_query';
import * as Schema from '../schemas/local.gql';
import URLHelpers from '../components/shared/url_helpers';
import CreditCardHtmlForm from './credit_card_html_form';
import SelectableCreditCard from './selectable_credit_card';
import SelectableCreditCardListPlaceholder from './selectable_credit_card_list_placeholder';
import SelectableCreditCardListError from './selectable_credit_card_list_error';
import AddNewCardButton from './add_new_card_button';
import CannotAddNewCardWarning from './cannot_add_new_card_warning';

export interface CreditCard extends core_apimessages_CreditCard {
  isSelected: boolean;
}

type SelectCardData = core_apimessages_CreditCard[];
interface SelectCardVariables {
  id: string;
}

interface ExternalProps {
  location: Location;
  checkoutBillingAddressForm?: React.ReactNode;
  belowCreditCardThreshold: boolean;
}

interface IState {
  isCreatingCard: boolean;
}

type MutationProps = MutateProps<SelectCardData, SelectCardVariables>;
type QueryProps = ChildProps<{}, SelectableCreditCardListQuery.Query, SelectableCreditCardListQuery.Variables>;
type IProps = ExternalProps & QueryProps & MutationProps;

function primaryCreditCard(creditCards: core_apimessages_CreditCard[]) {
  return creditCards.find(cc => cc.primaryForCheckout);
}

export function allCardsPrimaryFirst(creditCards: core_apimessages_CreditCard[]) {
  const allCards = compact([
    primaryCreditCard(creditCards),
  ]).concat(creditCards);

  return uniqBy(allCards, cc => cc.id) as CreditCard[];
}

function getCheckoutId(location) {
  const { pathname } = location;
  const parts = CHECKOUT_ID_MATCHER.exec(pathname);
  if (!parts || parts.length !== 2) {
    throw (new Error(`could not parse checkout id out of url ${pathname}`));
  }
  return parts[1]; // RCP-USD-direct_checkout
}

function cardEditPath(location: Location, creditCard: CreditCard) {
  const checkoutId = getCheckoutId(location);
  return URLHelpers.editDirectCheckoutCreditCardPath(checkoutId, creditCard.id);
}

export function selectCard(_, { id }, { client }) {
  const updated = updateQueryDataInline(client.readQuery({ query: QUERY }), id);
  client.writeQuery({ query: QUERY, data: updated });
  return null;
}

export function updateQueryDataInline(originalQueryData, id) {
  const updated = { ...originalQueryData };
  updated.me.creditCards = originalQueryData.me.creditCards.map((cc) => {
    if (cc.id === id) {
      return { ...cc, isSelected: true };
    }
    return { ...cc, isSelected: false };
  });
  return updated;
}

export const resolvers = {
  core_apimessages_CreditCard: {
    isSelected: (cc) => {
      return cc.primaryForCheckout;
    },
  },
};

const CHECKOUT_ID_MATCHER = new RegExp(/multi-checkout\/(.*)\/direct/);

export class SelectableCreditCardListContainer extends React.Component<IProps, IState> {
  constructor(props) {
    super(props);
    this.state = {
      isCreatingCard: false,
    };
  }

  static defaultProps = {
    location: window.location,
  };

  @bind
  updateSelectedCard(id) {
    this.props.mutate({ variables: { id } });
    this.setState({ isCreatingCard: !id }); // if id is present, close the form
  }

  renderNewCreditCardForm() {
    if (!this.state.isCreatingCard) { return null; }
    return (
      <div className="module">
        <h3 className="heading-3 mb-half">
          {I18n.t('discovery.selectableCreditCardList.creditCard')}
        </h3>
        <p className="small muted mb-1">
          <span className="fa fa-lock mr-space"></span>
          {I18n.t('discovery.selectableCreditCardList.processedWithEncryption')}
        </p>
        <CreditCardHtmlForm />
      </div>
    );
  }

  renderBillingAddress() {
    if (!this.state.isCreatingCard || !this.props.checkoutBillingAddressForm) { return null; }
    return this.props.checkoutBillingAddressForm;
  }

  renderAddNewCardOption() {
    const { belowCreditCardThreshold } = this.props;

    if (belowCreditCardThreshold) {
      return (
        <AddNewCardButton
          updateSelectedCard={this.updateSelectedCard}
          checked={this.state.isCreatingCard}
        />
      );
    }

    return <CannotAddNewCardWarning />;
  }

  render() {
    if (this.props.data.loading) return (<SelectableCreditCardListPlaceholder />);
    if (this.props.data.error) return (<SelectableCreditCardListError />);

    if (this.props.data.me.creditCards.length === 0) {
      throw (new Error('attempting to render card list, but no cards on file were found'));
    }

    const creditCardsInOrder = allCardsPrimaryFirst(this.props.data.me.creditCards);

    return (
      <div>
        <div className="module">
          <h3 className="heading-3 mb-1">
            {I18n.t('discovery.selectableCreditCardList.chooseACard')}
          </h3>
          <ul>
            {creditCardsInOrder.map((cc) => {
              return (
                <SelectableCreditCard
                  key={cc.id}
                  creditCard={cc}
                  onClick={() => this.updateSelectedCard(cc.id)}
                  editPath={cardEditPath(this.props.location, cc)}
                />
              );
            })}
            {this.renderAddNewCardOption()}
          </ul>
        </div>
        {this.renderNewCreditCardForm()}
        {this.renderBillingAddress()}
      </div>
    );
  }
}

export default flowRight([
  connectSelectableCreditCardListQuery,
  graphql<null, SelectCardData, SelectCardVariables>(Schema.SelectCheckoutCreditCard),
])(SelectableCreditCardListContainer);



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/discovery-ui/src/credit_cards/selectable_credit_card_list_container.tsx